import React from 'react';
import { PourConfig } from '../types';
import { translations } from '../utils/translations';
import { Ruler, Circle, Square, Scale, DollarSign, Layers } from 'lucide-react';

interface CalculatorInputProps {
  config: PourConfig;
  onChange: (newConfig: PourConfig) => void;
  t: typeof translations['en'];
}

const CalculatorInput: React.FC<CalculatorInputProps> = ({ config, onChange, t }) => {
  
  const handleDimensionChange = (field: keyof typeof config.dimensions, value: string) => {
    const numValue = parseFloat(value) || 0;
    onChange({
      ...config,
      dimensions: {
        ...config.dimensions,
        [field]: numValue
      }
    });
  };

  const handleRatioChange = (part: 'A' | 'B', value: string) => {
    const numValue = parseFloat(value) || 0;
    onChange({
      ...config,
      [part === 'A' ? 'ratioA' : 'ratioB']: numValue
    });
  };

  const inputClass = "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-700 placeholder-slate-400";
  const labelClass = "text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block";

  return (
    <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 md:p-8 space-y-8">
      
      {/* Unit Toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Ruler className="w-5 h-5 text-indigo-600" />
          {t.input.title}
        </h2>
        <div className="flex bg-slate-100 p-1.5 rounded-xl">
          <button
            onClick={() => onChange({ ...config, unitSystem: 'imperial' })}
            className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-all ${
              config.unitSystem === 'imperial' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.input.imperial}
          </button>
          <button
            onClick={() => onChange({ ...config, unitSystem: 'metric' })}
            className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-all ${
              config.unitSystem === 'metric' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.input.metric}
          </button>
        </div>
      </div>

      {/* Shape Selection */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => onChange({ ...config, shape: 'rectangle' })}
          className={`group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 transition-all ${
            config.shape === 'rectangle'
              ? 'border-indigo-600 bg-indigo-50/50 text-indigo-700'
              : 'border-slate-100 hover:border-indigo-200 text-slate-500'
          }`}
        >
          <Square className={`w-8 h-8 ${config.shape === 'rectangle' ? 'fill-indigo-600/20' : ''}`} />
          <span className="font-bold">{t.input.rectangle}</span>
        </button>
        <button
          onClick={() => onChange({ ...config, shape: 'circle' })}
          className={`group flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 transition-all ${
            config.shape === 'circle'
              ? 'border-indigo-600 bg-indigo-50/50 text-indigo-700'
              : 'border-slate-100 hover:border-indigo-200 text-slate-500'
          }`}
        >
          <Circle className={`w-8 h-8 ${config.shape === 'circle' ? 'fill-indigo-600/20' : ''}`} />
          <span className="font-bold">{t.input.circle}</span>
        </button>
      </div>

      {/* Dimensions Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {config.shape === 'rectangle' ? (
          <>
            <div>
              <label className={labelClass}>{t.input.length} ({config.unitSystem === 'imperial' ? 'in' : 'cm'})</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={config.dimensions.length || ''}
                onChange={(e) => handleDimensionChange('length', e.target.value)}
                className={inputClass}
                placeholder="0.0"
              />
            </div>
            <div>
              <label className={labelClass}>{t.input.width} ({config.unitSystem === 'imperial' ? 'in' : 'cm'})</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={config.dimensions.width || ''}
                onChange={(e) => handleDimensionChange('width', e.target.value)}
                className={inputClass}
                placeholder="0.0"
              />
            </div>
          </>
        ) : (
          <div>
            <label className={labelClass}>{t.input.diameter} ({config.unitSystem === 'imperial' ? 'in' : 'cm'})</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={config.dimensions.diameter || ''}
              onChange={(e) => handleDimensionChange('diameter', e.target.value)}
              className={inputClass}
              placeholder="0.0"
            />
          </div>
        )}
        
        <div>
          <label className={labelClass}>{t.input.depth} ({config.unitSystem === 'imperial' ? 'in' : 'cm'})</label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={config.dimensions.depth || ''}
            onChange={(e) => handleDimensionChange('depth', e.target.value)}
            className={inputClass}
            placeholder="0.0"
          />
        </div>
      </div>

      <div className="border-t border-slate-100 my-6"></div>

      {/* Advanced Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Mixing Ratio */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-800 font-bold">
            <Scale className="w-5 h-5 text-indigo-600" />
            <h3>{t.input.ratio}</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">{t.input.ratioPartA}</span>
              <input
                type="number"
                min="1"
                value={config.ratioA}
                onChange={(e) => handleRatioChange('A', e.target.value)}
                className={`${inputClass} pl-8 text-center`}
              />
            </div>
            <span className="text-slate-300 font-bold text-xl">:</span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">{t.input.ratioPartB}</span>
              <input
                type="number"
                min="1"
                value={config.ratioB}
                onChange={(e) => handleRatioChange('B', e.target.value)}
                className={`${inputClass} pl-8 text-center`}
              />
            </div>
          </div>
          <p className="text-xs text-slate-400 font-medium">{t.input.ratioExample}</p>
        </div>

        {/* Cost Estimator */}
        <div className="space-y-4">
           <div className="flex items-center gap-2 text-slate-800 font-bold">
            <DollarSign className="w-5 h-5 text-green-600" />
            <h3>{t.input.cost}</h3>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-2 block">
              {config.unitSystem === 'imperial' ? t.input.pricePerGallon : t.input.pricePerLiter}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                min="0"
                value={config.pricePerUnit || ''}
                onChange={(e) => onChange({...config, pricePerUnit: parseFloat(e.target.value) || 0})}
                className={`${inputClass} pl-8`}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Waste Margin Slider */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
        <div className="flex justify-between items-center mb-4">
           <div className="flex items-center gap-2">
             <Layers className="w-4 h-4 text-indigo-500" />
             <span className="text-sm font-bold text-slate-700">{t.input.wasteMargin}</span>
           </div>
           <span className="text-sm font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md">{config.wasteMargin}%</span>
        </div>
        <input 
          type="range" 
          min="0" 
          max="20" 
          step="1"
          value={config.wasteMargin}
          onChange={(e) => onChange({...config, wasteMargin: parseInt(e.target.value)})}
          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
        />
      </div>

    </div>
  );
};

export default CalculatorInput;